import React from 'react';

import { Link } from '~/lib/react-router-dom';

import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Breadcrumbs, Skeleton, Typography } from '@mui/material';

import useOfferBreadcrumbs, { BreadcrumbItem } from '~/hooks/useOfferBreadcrumbs';

interface OfferBreadcrumbsProps {
  /** The offer ID to fetch breadcrumb data for */
  offerId?: string | null;
  /** The current page name (displayed as the last breadcrumb, not linked). If empty, the last entity becomes the current page. */
  currentPage?: string;
  /** Pre-loaded vendor data (use when vendor is already fetched) */
  vendor?: BreadcrumbItem | null;
  /** Pre-loaded property data (use when property is already fetched) */
  property?: BreadcrumbItem | null;
  /** Pre-loaded offer data (use when offer is already fetched) */
  offer?: BreadcrumbItem | null;
  /** Whether this is a multi-vendor context (for Bundle and Save) */
  isMultiVendor?: boolean;
  /** Whether this is a multi-property context */
  isMultiProperty?: boolean;
}

// Helper functions to create breadcrumb elements (not components to satisfy one-component-per-file rule)
const renderBreadcrumbLink = (key: string, href: string, children: React.ReactNode) => (
  <Link key={key} to={href} style={{ textDecoration: 'none' }}>
    <Typography color="primary" sx={{ '&:hover': { textDecoration: 'underline' } }}>
      {children}
    </Typography>
  </Link>
);

const renderBreadcrumbText = (key: string, children: React.ReactNode) => (
  <Typography key={key} color="text.secondary">
    {children}
  </Typography>
);

const renderCurrentPage = (key: string, children: React.ReactNode) => (
  <Typography key={key} color="text.primary" fontWeight={500}>
    {children}
  </Typography>
);

export default function OfferBreadcrumbs({
  offerId,
  currentPage = '',
  vendor: vendorProp,
  property: propertyProp,
  offer: offerProp,
  isMultiVendor: isMultiVendorProp = false,
  isMultiProperty: isMultiPropertyProp = false,
}: OfferBreadcrumbsProps) {
  // Only fetch if we don't have pre-loaded data
  const shouldFetch = Boolean(offerId) && !vendorProp && !offerProp;

  const { data, isLoading } = useOfferBreadcrumbs({
    offerId: offerId ?? null,
    enabled: shouldFetch,
  });

  // Use pre-loaded data if available, otherwise use fetched data
  const vendor = vendorProp ?? data?.vendor ?? null;
  const property = propertyProp ?? data?.property ?? null;
  const offer = offerProp ?? data?.offer ?? null;
  const isMultiVendor = isMultiVendorProp || data?.isMultiVendor || false;
  const isMultiProperty = isMultiPropertyProp || data?.isMultiProperty || false;

  if (shouldFetch && isLoading) {
    return (
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 1 }}>
        <Skeleton width={60} />
        <Skeleton width={100} />
        <Skeleton width={80} />
        <Skeleton width={120} />
      </Breadcrumbs>
    );
  }

  // Determine what is the "final" item (the current page entity)
  // If currentPage is provided, that's the final item
  // Otherwise, the last entity in the hierarchy becomes the current page
  const hasCurrentPage = currentPage.length > 0;

  const items: Array<React.ReactNode> = [];

  // Determine what's the "end" of the hierarchy
  const isVendorFinal = vendor && !property && !offer && !hasCurrentPage;
  const isPropertyFinal = property && !offer && !hasCurrentPage;
  const isOfferFinal = offer && !hasCurrentPage;

  // Add "Vendors" section link (always if we have vendor)
  if (vendor) {
    items.push(renderBreadcrumbLink('vendors-section', '/vendors', 'Vendors'));

    // Add vendor name - link it unless it's the final item
    if (isMultiVendor) {
      items.push(renderBreadcrumbText('vendor-name', vendor.label));
    } else if (isVendorFinal) {
      items.push(renderCurrentPage('vendor-name', vendor.label));
    } else if (vendor.href) {
      items.push(renderBreadcrumbLink('vendor-name', vendor.href, vendor.label));
    }
  }

  // Add "Properties" section and property name
  if (property && !isVendorFinal) {
    if (isMultiProperty) {
      items.push(renderBreadcrumbText('properties-section', property.label));
    } else {
      // Add "Properties" section link (links to vendor page which shows properties)
      if (vendor?.href) {
        items.push(renderBreadcrumbLink('properties-section', vendor.href, 'Properties'));
      }

      // Add property name - link it unless it's the final item
      if (isPropertyFinal) {
        items.push(renderCurrentPage('property-name', property.label));
      } else if (property.href) {
        items.push(renderBreadcrumbLink('property-name', property.href, property.label));
      }
    }
  }

  // Add "Offers" section and offer name
  if (offer && !isVendorFinal && !isPropertyFinal) {
    items.push(renderBreadcrumbLink('offers-section', '/offers', 'Offers'));

    // Add offer name - link it unless it's the final item
    if (isOfferFinal) {
      items.push(renderCurrentPage('offer-name', offer.label));
    } else if (offer.href) {
      items.push(renderBreadcrumbLink('offer-name', offer.href, offer.label));
    }
  }

  // Add explicit current page if provided
  if (hasCurrentPage) {
    items.push(renderCurrentPage('current', currentPage));
  }

  if (items.length <= 1) {
    return null;
  }

  return (
    <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mt: '10px', mb: 1 }}>
      {items}
    </Breadcrumbs>
  );
}
